/**
 * "Dispatch a fix" (spec section 7): a Claude Code session in the project's main checkout on
 * that machine, with the diagnosis attached, so the fix follows the project's own rules. The
 * person sees what the session starts with and where, adds a note if they like, and starts it.
 */
import { SquareTerminal } from '@orgus/ui/icons';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, Checkbox, Dialog, ErrorState, RelativeTime, RowStatus, Textarea, shortPath, toUiError } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { dispatchFix, usePpSlice } from './api.ts';
import { dispatchBlocker, mainCheckout, problemOrigin, problemSource, problemStatus } from './model.ts';
import { closePpDialogs, usePpDialogs } from './store.ts';
import { WhyNext } from './Problems.tsx';

export function DispatchDialogHost() {
  const key = usePpDialogs((s) => s.dispatch);
  const state = usePpSlice();
  const projects = useOrgus((s) => s.projects);
  const machines = useOrgus((s) => s.machines);
  const checkouts = useOrgus((s) => s.checkouts);
  const navigate = useNavigate();
  const [note, setNote] = useState('');
  const [terminal, setTerminal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const [dispatchId, setDispatchId] = useState<string | null>(null);

  useEffect(() => {
    setNote('');
    setTerminal(false);
    setError(null);
    setBusy(false);
    setDispatchId(null);
  }, [key]);

  const problem = key ? state?.problems.find((p) => p.key === key) : undefined;
  const main = useMemo(
    () => (problem?.projectId && problem.machineId ? mainCheckout(Object.values(checkouts), problem.projectId, problem.machineId) : undefined),
    [checkouts, problem?.projectId, problem?.machineId],
  );
  const dispatch = dispatchId ? state?.dispatches.find((d) => d.id === dispatchId) : undefined;
  const close = () => closePpDialogs();
  if (!key) return null;

  if (!problem) {
    return (
      <Dialog open onOpenChange={(v) => !v && close()} title="Problem cleared" size="sm" footer={<Button onClick={close}>Close</Button>}>
        <p className="text-ui text-fg-muted">This problem is no longer open, so there is nothing to fix.</p>
      </Dialog>
    );
  }

  const source = problemSource(problem, (id) => projects[id]?.name);
  const blocker = dispatchBlocker(problem, main);
  const machine = problem.machineId ? machines[problem.machineId] : undefined;
  const openSession = (id: string) => {
    close();
    navigate(`/sessions?session=${encodeURIComponent(id)}`);
  };
  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const d = await dispatchFix(problem.key, note, terminal ? 'terminal' : 'headless');
      setDispatchId(d.id);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };

  const footer = dispatch ? (
    <>
      <Button variant="secondary" onClick={close}>
        Close
      </Button>
      {dispatch.state === 'started' && dispatch.sessionId && (
        <Button variant="primary" icon={<SquareTerminal />} onClick={() => openSession(dispatch.sessionId!)}>
          Open session
        </Button>
      )}
    </>
  ) : (
    <>
      <Button variant="secondary" disabled={busy} onClick={close}>
        Cancel
      </Button>
      <Button variant="primary" loading={busy} disabled={!!blocker} onClick={() => void start()}>
        Start fix session
      </Button>
    </>
  );

  return (
    <Dialog open onOpenChange={(v) => !busy && !v && close()} dismissible={!busy} title="Dispatch a fix" description="Starts Claude Code in the project with this diagnosis" footer={footer}>
      <div className="flex flex-col gap-4">
        <section aria-label="Diagnosis" className="flex flex-col gap-2 rounded-card border border-border bg-bg p-3">
          <div className="flex min-w-0 items-center gap-2">
            <RowStatus {...problemStatus(problem.severity)} hideLabel focusable={false} />
            <span className="min-w-0 truncate text-ui font-medium text-fg">{problem.what}</span>
          </div>
          <WhyNext why={problem.why} next={problem.next} />
          <p className="flex flex-wrap gap-x-3 text-caption text-fg-subtle">
            <span>
              {source}, {problemOrigin(problem)}
            </span>
            <RelativeTime ts={problem.since} prefix="Since" />
          </p>
        </section>

        <dl className="grid gap-3 sm:grid-cols-2">
          <div className="flex min-w-0 flex-col gap-1">
            <dt className="text-caption text-fg-subtle">Folder</dt>
            <dd className="truncate font-mono text-caption text-fg-muted" title={main?.path}>
              {main?.path ? shortPath(main.path, 40) : 'None'}
            </dd>
          </div>
          <div className="flex flex-col gap-1">
            <dt className="text-caption text-fg-subtle">Machine</dt>
            <dd className="text-ui text-fg-muted">{machine?.name ?? 'None'}</dd>
          </div>
        </dl>

        {blocker && !dispatch && <ErrorState variant="inline" error={{ what: 'No fix session for this problem.', why: blocker, next: 'Open what the problem is about and fix it there.' }} />}

        {dispatch ? (
          <div className="flex flex-col gap-2" aria-live="polite">
            {dispatch.state === 'starting' && <RowStatus tone="working" icon="working" label={`Starting on ${machine?.name ?? 'the machine'}`} />}
            {dispatch.state === 'started' && <RowStatus tone="success" icon="done" label="Fix session started" />}
            {dispatch.state === 'failed' && (
              <ErrorState
                variant="inline"
                error={{ what: 'The fix session did not start.', why: dispatch.reason ?? 'The machine did not say why.', next: 'Check the machine, then dispatch again.' }}
              />
            )}
          </div>
        ) : (
          !blocker && (
            <>
              <Textarea label="Note" placeholder="Check the deploy token first" rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
              <Checkbox label="Open in a terminal window" checked={terminal} onCheckedChange={setTerminal} />
            </>
          )
        )}
        {error && <ErrorState variant="inline" error={error} />}
      </div>
    </Dialog>
  );
}
